// SkinExpert/src/app/layout/account/AccountManagement.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  Chip,
  Divider,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Avatar,
  Grid,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import SecurityIcon from "@mui/icons-material/Security";
import AccountService, {
  UserProfile,
  UpdateProfileData,
  ChangeEmailData,
  ChangePasswordData,
} from "../../services/AccountService";
import AuthService from "../../services/AuthService";
import { useNavigate } from "react-router-dom";
import SubscriptionManagement from "./SubscriptionManagement";
import TeamManagement from "./TeamManagement";
import AgentBuilder from "./AgentBuilder";
import { useSubscription } from "../../hooks/useSubscription";
import PeopleIcon from "@mui/icons-material/People";
import SmartToyIcon from "@mui/icons-material/SmartToy";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function AccountManagement() {
  const { isAdmin, isProfessional, loading: subscriptionLoading } = useSubscription();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const navigate = useNavigate();
  const authService = new AuthService();

  

  // Form states
  const [profileForm, setProfileForm] = useState({
    userName: "",
    phoneNumber: "",
  });

  const [emailForm, setEmailForm] = useState({
    newEmail: "",
    currentPassword: "",
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [deletePassword, setDeletePassword] = useState("");

  useEffect(() => {
    loadProfile();
  }, []);

const loadProfile = async () => {
  try {
    setLoading(true);
    setError(null); // Clear previous errors
    const data = await AccountService.getProfile();
    console.log("Profile loaded:", data); // Debug log
    console.log(authService.getAccessToken());
    setProfile(data);
    setProfileForm({
      userName: data.userName || "",
      phoneNumber: data.phoneNumber || "",
    });
  } catch (err: any) {
    console.error("Profile load error:", err); // Debug log
    setError(err.message || "Failed to load profile");
  } finally {
    setLoading(false);
  }
};

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const data: UpdateProfileData = {
        userName: profileForm.userName,
        phoneNumber: profileForm.phoneNumber,
      };
      const result = await AccountService.updateProfile(data);
      setSuccess(result.message);
      await loadProfile();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleChangeEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      const data: ChangeEmailData = {
        newEmail: emailForm.newEmail,
        currentPassword: emailForm.currentPassword,
      };
      const result = await AccountService.changeEmail(data);
      setSuccess(result.message);
      setEmailForm({ newEmail: "", currentPassword: "" });
      await loadProfile();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError("New passwords do not match");
      return;
    }

    try {
      const data: ChangePasswordData = {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
        confirmPassword: passwordForm.confirmPassword,
      };
      const result = await AccountService.changePassword(data);
      setSuccess(result.message);
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteAccount = async () => {
    setError(null);
    setSuccess(null);

    try {
      await AccountService.deleteAccount(deletePassword);
      setDeleteDialogOpen(false);
      alert("Account deleted successfully");
      authService.logout();
      navigate("/");
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box 
      maxWidth="1400px" 
      mx="auto" 
      mt={4} 
      px={2}
      sx={{
        background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.03) 0%, rgba(168, 85, 247, 0.03) 100%)',
        minHeight: '100vh',
        pb: 6,
      }}
    >
      <Box textAlign="center" mb={6} pt={4}>
        <Typography 
          variant="h3" 
          gutterBottom
          sx={{ 
            fontWeight: 700,
            background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Account Settings
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
          Manage your profile, security, and subscription
        </Typography>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            boxShadow: '0 2px 10px rgba(239, 68, 68, 0.1)',
          }} 
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert 
          severity="success" 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            boxShadow: '0 2px 10px rgba(34, 197, 94, 0.1)',
          }} 
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Card
        sx={{
          borderRadius: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          {profile && (
            <>
              {/* Profile Header */}
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                  color: 'white',
                  p: 4,
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'radial-gradient(circle at top right, rgba(255,255,255,0.1) 0%, transparent 60%)',
                  },
                }}
              >
                <Box display="flex" alignItems="center" gap={3} position="relative">
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: 'rgba(255,255,255,0.2)',
                      border: '3px solid rgba(255,255,255,0.3)',
                      fontSize: '2rem',
                      fontWeight: 700,
                    }}
                  >
                    {profile.userName?.charAt(0).toUpperCase() || profile.email.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      {profile.userName || 'User'}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <EmailIcon sx={{ fontSize: 18 }} />
                      <Typography variant="body1">{profile.email}</Typography>
                      {profile.emailConfirmed && (
                        <VerifiedUserIcon sx={{ fontSize: 18, ml: 1 }} />
                      )}
                    </Box>
                    <Box display="flex" gap={1} flexWrap="wrap" mt={2}>
                      <Chip
                        label={profile.emailConfirmed ? "Verified" : "Unverified"}
                        size="small"
                        sx={{
                          bgcolor: profile.emailConfirmed ? 'rgba(34, 197, 94, 0.2)' : 'rgba(251, 146, 60, 0.2)',
                          color: 'white',
                          fontWeight: 600,
                          border: '1px solid rgba(255,255,255,0.2)',
                        }}
                      />
                      <Chip
                        label={profile.isApproved ? "Approved" : "Pending"}
                        size="small"
                        sx={{
                          bgcolor: profile.isApproved ? 'rgba(34, 197, 94, 0.2)' : 'rgba(251, 146, 60, 0.2)',
                          color: 'white',
                          fontWeight: 600,
                          border: '1px solid rgba(255,255,255,0.2)',
                        }}
                      />
                      {profile.roles.map((role: any) => (
                        <Chip
                          key={role}
                          label={role}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.2)',
                            color: 'white',
                            fontWeight: 600,
                            border: '1px solid rgba(255,255,255,0.2)',
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: "divider", bgcolor: 'background.paper' }}>
                <Tabs 
                  value={tabValue} 
                  onChange={(_, v) => setTabValue(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      minHeight: 64,
                      px: 3,
                    },
                    '& .Mui-selected': {
                      color: 'primary.main',
                    },
                  }}
                >
                  <Tab icon={<AccountCircleIcon />} iconPosition="start" label="Profile" />
                  <Tab icon={<CreditCardIcon />} iconPosition="start" label="Subscription" />
                  {isAdmin && <Tab icon={<PeopleIcon />} iconPosition="start" label="Team" />}
                  {isProfessional && (
                    <Tab 
                      icon={<SmartToyIcon />} 
                      iconPosition="start" 
                      label="AI Agents"
                      sx={{
                        '& .MuiTab-iconWrapper': {
                          animation: 'pulse 2s infinite',
                        },
                        '@keyframes pulse': {
                          '0%, 100%': { opacity: 1 },
                          '50%': { opacity: 0.6 },
                        },
                      }}
                    />
                  )}
                  <Tab icon={<EmailIcon />} iconPosition="start" label="Change Email" />
                  <Tab icon={<LockIcon />} iconPosition="start" label="Change Password" />
                  <Tab icon={<SecurityIcon />} iconPosition="start" label="Security (2FA)" />
                  <Tab icon={<DeleteForeverIcon />} iconPosition="start" label="Delete Account" />
                </Tabs>
              </Box>

              {/* Profile Tab */}
              <TabPanel value={tabValue} index={0}>
                <Box maxWidth="600px" mx="auto">
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                    Update Your Profile
                  </Typography>
                  <form onSubmit={handleUpdateProfile}>
                    <TextField
                      fullWidth
                      label="Username"
                      value={profileForm.userName}
                      onChange={(e) =>
                        setProfileForm({ ...profileForm, userName: e.target.value })
                      }
                      margin="normal"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={profileForm.phoneNumber}
                      onChange={(e) =>
                        setProfileForm({ ...profileForm, phoneNumber: e.target.value })
                      }
                      margin="normal"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <Button 
                      type="submit" 
                      variant="contained" 
                      size="large"
                      sx={{ 
                        mt: 3,
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '1rem',
                        background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #4f46e5 0%, #9333ea 100%)',
                        },
                      }}
                    >
                      Update Profile
                    </Button>
                  </form>
                </Box>
              </TabPanel>

              {/* Subscription Tab */}
              <TabPanel value={tabValue} index={1}>
                <SubscriptionManagement />
              </TabPanel>

              {/* Team Management Tab - Only for admins */}
              {isAdmin && (
                <TabPanel value={tabValue} index={2}>
                  <TeamManagement />
                </TabPanel>
              )}

              {/* AI Agent Builder Tab - Only for professional tier */}
              {isProfessional && (
                <TabPanel value={tabValue} index={isAdmin ? 3 : 2}>
                  <AgentBuilder />
                </TabPanel>
              )}

              {/* Change Email Tab */}
              <TabPanel value={tabValue} index={2 + (isAdmin ? 1 : 0) + (isProfessional ? 1 : 0)}>
                <Box maxWidth="600px" mx="auto">
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                    Change Email Address
                  </Typography>
                  <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                    You'll need to verify your new email address after changing it.
                  </Alert>
                  <form onSubmit={handleChangeEmail}>
                    <TextField
                      fullWidth
                      label="New Email"
                      type="email"
                      value={emailForm.newEmail}
                      onChange={(e) =>
                        setEmailForm({ ...emailForm, newEmail: e.target.value })
                      }
                      margin="normal"
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Current Password"
                      type="password"
                      value={emailForm.currentPassword}
                      onChange={(e) =>
                        setEmailForm({ ...emailForm, currentPassword: e.target.value })
                      }
                      margin="normal"
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <Button 
                      type="submit" 
                      variant="contained" 
                      size="large"
                      sx={{ 
                        mt: 3,
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '1rem',
                        background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #4f46e5 0%, #9333ea 100%)',
                        },
                      }}
                    >
                      Change Email
                    </Button>
                  </form>
                </Box>
              </TabPanel>

              {/* Change Password Tab */}
              <TabPanel value={tabValue} index={3 + (isAdmin ? 1 : 0) + (isProfessional ? 1 : 0)}>
                <Box maxWidth="600px" mx="auto">
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                    Change Password
                  </Typography>
                  <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                    Use a strong password with at least 8 characters, including numbers and special characters.
                  </Alert>
                  <form onSubmit={handleChangePassword}>
                    <TextField
                      fullWidth
                      label="Current Password"
                      type="password"
                      value={passwordForm.currentPassword}
                      onChange={(e) =>
                        setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                      }
                      margin="normal"
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      label="New Password"
                      type="password"
                      value={passwordForm.newPassword}
                      onChange={(e) =>
                        setPasswordForm({ ...passwordForm, newPassword: e.target.value })
                      }
                      margin="normal"
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Confirm New Password"
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={(e) =>
                        setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                      }
                      margin="normal"
                      required
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                    <Button 
                      type="submit" 
                      variant="contained" 
                      size="large"
                      sx={{ 
                        mt: 3,
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '1rem',
                        background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #4f46e5 0%, #9333ea 100%)',
                        },
                      }}
                    >
                      Change Password
                    </Button>
                  </form>
                </Box>
              </TabPanel>

              {/* Two-Factor Authentication Tab */}
              <TabPanel value={tabValue} index={4 + (isAdmin ? 1 : 0) + (isProfessional ? 1 : 0)}>
                <Box maxWidth="800px" mx="auto">
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                    Two-Factor Authentication
                  </Typography>
                  <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      🔒 Enhance Your Account Security
                    </Typography>
                    <Typography variant="body2">
                      Two-factor authentication adds an extra layer of security to your account. 
                      Choose between email codes (default) or authenticator app for instant, offline codes.
                    </Typography>
                  </Alert>

                  <Card
                    sx={{
                      border: '2px solid',
                      borderColor: 'primary.main',
                      borderRadius: 2,
                      p: 4,
                      background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)',
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={2} mb={3}>
                      <SecurityIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                      <Box>
                        <Typography variant="h6" fontWeight={600}>
                          Manage Your 2FA Settings
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Configure email or authenticator app verification
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            p: 3,
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <EmailIcon sx={{ fontSize: 32, color: 'primary.main', mb: 2 }} />
                          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            Email Verification
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Receive 6-digit codes via email. Fast delivery (200-500ms) with our optimized system.
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box
                          sx={{
                            p: 3,
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <SecurityIcon sx={{ fontSize: 32, color: 'success.main', mb: 2 }} />
                          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                            Authenticator App
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Use Google Authenticator or similar apps for instant, offline codes. More secure!
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    <Box mt={4} textAlign="center">
                      <Button
                        variant="contained"
                        size="large"
                        startIcon={<SecurityIcon />}
                        onClick={() => {
                          // Redirect to IdentityServer 2FA management with return URL
                          // Uses OIDC authority from environment, maintains authenticated session
                          const authority = process.env.REACT_APP_AUTHORITY;
                          const returnUrl = encodeURIComponent(window.location.origin + '/account');
                          window.location.href = `${authority}/Account/ManageTwoFactor?returnUrl=${returnUrl}`;
                        }}
                        sx={{
                          px: 4,
                          py: 1.5,
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '1rem',
                          background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #4f46e5 0%, #9333ea 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 20px rgba(99, 102, 241, 0.3)',
                          },
                          transition: 'all 0.3s ease',
                        }}
                      >
                        Configure Two-Factor Authentication
                      </Button>
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 2 }}>
                        You'll be redirected to the secure authentication server
                      </Typography>
                    </Box>
                  </Card>
                </Box>
              </TabPanel>

              {/* Delete Account Tab */}
              <TabPanel value={tabValue} index={5 + (isAdmin ? 1 : 0) + (isProfessional ? 1 : 0)}>
                <Box maxWidth="600px" mx="auto">
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3, color: 'error.main' }}>
                    Delete Account
                  </Typography>
                  <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      ⚠️ Warning: This action cannot be undone!
                    </Typography>
                    <Typography variant="body2">
                      Deleting your account will permanently remove all your data, including your profile, 
                      subscription, and all associated information. This action is irreversible.
                    </Typography>
                  </Alert>
                  <Card
                    sx={{
                      border: '2px solid',
                      borderColor: 'error.main',
                      borderRadius: 2,
                      p: 3,
                      bgcolor: 'rgba(239, 68, 68, 0.05)',
                    }}
                  >
                    <Typography variant="body1" gutterBottom fontWeight={600}>
                      Before you proceed:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                      <Typography component="li" variant="body2">
                        Make sure you've downloaded any important data
                      </Typography>
                      <Typography component="li" variant="body2">
                        Cancel any active subscriptions
                      </Typography>
                      <Typography component="li" variant="body2">
                        Understand that this action is permanent
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      color="error"
                      size="large"
                      onClick={() => setDeleteDialogOpen(true)}
                      sx={{
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4,
                        py: 1.5,
                        borderWidth: 2,
                        '&:hover': {
                          borderWidth: 2,
                          bgcolor: 'error.main',
                          color: 'white',
                        },
                      }}
                    >
                      Delete My Account
                    </Button>
                  </Card>
                </Box>
              </TabPanel>
            </>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: 400,
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, fontSize: '1.5rem' }}>
          Confirm Account Deletion
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            This action is permanent and cannot be undone!
          </Alert>
          <Typography gutterBottom sx={{ mb: 2 }}>
            Are you absolutely sure? This will permanently delete your account and all
            associated data.
          </Typography>
          <TextField
            fullWidth
            label="Enter your password to confirm"
            type="password"
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
            margin="normal"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteAccount} 
            color="error" 
            variant="contained" 
            disabled
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
            }}
          >
            Delete Account
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}